// Port of ios/Sources/Calls/CallRtcSession.swift (Android `CallRtcSession`), on the browser's own
// RTCPeerConnection. Same dance, from calls-common's rtc_peer.js:
//  - Creating the data channel FIRST is what produces the initial offer — the connection is
//    negotiated before any media track exists, and the first unmute adds the mic track later.
//  - The SFU sends OFFERS of its own whenever it has new remote tracks for us; we answer.
//  - After the first answer settles, any renegotiation WE initiate (mic on first unmute, screen,
//    camera) must hold the server's signaling lock: data-channel Lock, wait for granted,
//    renegotiate, Unlock after our next answer is applied.
//  - Mute is `replaceTrack(null)` — no renegotiation, so no lock — matching the webapp, which stops
//    sending packets instead of sending silence (Apple: `sender.track = nil`).
//  - Remote video is classified by its TRACK ID (`<type>_<senderSession>_<rand>`, rtcd's
//    genTrackID); the media map (mid → type) is only the fallback for the type.
// Everything is serialized on one promise chain: interleaving createOffer/setRemote is exactly how
// signaling-state errors happen (the natives use one dispatch queue for the same reason).
import { decodeDcMessage, encodeDcLock, encodeDcUnlock, remoteTrackInfo } from '../../../core/callsCodec';
import type { CallIceServer } from '../../../core/callIceServer';

export interface CallRtcListener {
  localSdp(json: string): void;
  localCandidate(json: string): void;
  /** The media transport reached CONNECTED — audio is actually flowing. */
  connected(): void;
  remoteScreen(stream: MediaStream | null): void;
  /** Remote CAMERAS keyed by the sender's session (from the track id). */
  remoteVideos(bySession: Record<string, MediaStream>): void;
  /** A remote audio track to play (one per speaker the SFU forwards). */
  remoteAudio(track: MediaStreamTrack): void;
  connectionState(state: string): void;
  failed(reason: string): void;
}

export class CallRtcSession {
  private static readonly lockRetryDelayMillis = 50;
  private static readonly lockMaxAttempts = 100;

  pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private micTrack: MediaStreamTrack | null = null;
  private audioSender: RTCRtpSender | null = null;
  private screenSender: RTCRtpSender | null = null;
  private cameraSender: RTCRtpSender | null = null;
  private mediaMap: Record<string, string> = {};
  private remoteVideoByMid = new Map<string, MediaStreamTrack>();
  private streamsByTrack = new Map<string, MediaStream>();
  private dcNegotiated = false;
  private lockResponse: ((acquired: boolean) => void) | null = null;
  private unlockAfterAnswer = false;
  private closed = false;
  private chain: Promise<unknown> = Promise.resolve();
  private pendingCandidates: RTCIceCandidateInit[] = [];

  constructor(
    private readonly iceServers: CallIceServer[],
    private readonly listener: CallRtcListener,
  ) {}

  private run<T>(job: () => Promise<T> | T): Promise<T> {
    const next = this.chain.then(() => (this.closed ? (undefined as T) : job()));
    this.chain = next.catch(() => undefined);
    return next;
  }

  start(): void {
    const pc = new RTCPeerConnection({
      iceServers: this.iceServers.map((s) => ({ urls: s.urls, username: s.username, credential: s.credential })),
      bundlePolicy: 'max-bundle',
    });
    this.pc = pc;
    pc.onnegotiationneeded = () => void this.run(() => this.offer());
    pc.onicecandidate = (e) => {
      if (!e.candidate || !e.candidate.candidate) return;
      this.listener.localCandidate(JSON.stringify({ candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid ?? '', sdpMLineIndex: e.candidate.sdpMLineIndex ?? 0 }));
    };
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      this.listener.connectionState(state);
      if (state === 'connected') this.listener.connected();
      else if (state === 'failed' && !this.closed) this.listener.failed('media connection failed');
    };
    pc.ontrack = (e) => this.onTrack(e);
    const dc = pc.createDataChannel('calls-dc');
    dc.binaryType = 'arraybuffer';
    dc.onmessage = (e) => void this.onDcMessage(e.data);
    this.dc = dc;
    // Creating the channel flags negotiation-needed: the initial offer comes from there.
  }

  close(): void {
    this.closed = true;
    this.lockResponse = null;
    for (const sender of [this.screenSender, this.cameraSender]) sender?.track?.stop();
    this.micTrack?.stop();
    this.micTrack = null;
    this.audioSender = this.screenSender = this.cameraSender = null;
    this.remoteVideoByMid.clear();
    try {
      this.dc?.close();
    } catch {
      /* closing */
    }
    try {
      this.pc?.close();
    } catch {
      /* closing */
    }
    this.dc = null;
    this.pc = null;
  }

  // MARK: - Signaling in

  handleRemoteSignal(json: string): void {
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(json);
    } catch {
      return;
    }
    void this.run(async () => {
      const pc = this.pc;
      if (!pc) return;
      switch (message.type) {
        case 'answer':
          try {
            await pc.setRemoteDescription({ type: 'answer', sdp: String(message.sdp ?? '') });
          } catch {
            return; // a stale answer; the next negotiation recovers
          }
          await this.flushCandidates();
          this.pruneInactiveVideo();
          if (!this.dcNegotiated) this.dcNegotiated = true;
          else if (this.unlockAfterAnswer) {
            this.unlockAfterAnswer = false;
            this.sendDc(encodeDcUnlock());
          }
          return;
        case 'offer':
          try {
            await pc.setRemoteDescription({ type: 'offer', sdp: String(message.sdp ?? '') });
          } catch (error) {
            if (!this.closed) this.listener.failed(`setRemote offer: ${String(error)}`);
            return;
          }
          await this.flushCandidates();
          await this.answer();
          this.pruneInactiveVideo();
          return;
        case 'candidate': {
          const c = (message.candidate && typeof message.candidate === 'object' ? message.candidate : {}) as Record<string, unknown>;
          if (typeof c.candidate !== 'string') return;
          const init: RTCIceCandidateInit = { candidate: c.candidate, sdpMid: typeof c.sdpMid === 'string' ? c.sdpMid : null, sdpMLineIndex: typeof c.sdpMLineIndex === 'number' ? c.sdpMLineIndex : 0 };
          if (!pc.remoteDescription) this.pendingCandidates.push(init);
          else await pc.addIceCandidate(init).catch(() => undefined);
          return;
        }
      }
    });
  }

  private async flushCandidates(): Promise<void> {
    const pending = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of pending) await this.pc?.addIceCandidate(c).catch(() => undefined);
  }

  // MARK: - Microphone

  /** The mic exists from the first unmute; the caller (the manager) owns permission and capture. */
  get hasLiveMic(): boolean {
    return !!this.micTrack && this.micTrack.readyState === 'live';
  }

  /** True mute stops SENDING, not just silences: no packets leave the device at all. */
  async setMuted(muted: boolean, freshMic?: MediaStreamTrack): Promise<void> {
    const firstAttach = await this.run(async () => {
      if (freshMic && freshMic !== this.micTrack) {
        this.micTrack?.stop();
        this.micTrack = freshMic;
      }
      const track = this.micTrack;
      if (!track) return false;
      if (muted) {
        await this.audioSender?.replaceTrack(null);
        track.enabled = false;
        return false;
      }
      track.enabled = true;
      if (this.audioSender) {
        await this.audioSender.replaceTrack(track);
        return false;
      }
      return true;
    });
    // First unmute: the mic track joins the connection, which renegotiates — under lock.
    if (firstAttach) {
      await this.withSignalingLock(() => {
        const track = this.micTrack;
        if (this.pc && track && !this.audioSender) this.audioSender = this.pc.addTrack(track, new MediaStream([track]));
      });
    }
  }

  // MARK: - Screen share and camera (the stream id was announced BEFORE: screen_on / video_on)

  startScreenShare(stream: MediaStream): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track || this.screenSender) return Promise.resolve();
    return this.withSignalingLock(() => {
      if (this.pc && !this.screenSender) this.screenSender = this.pc.addTrack(track, stream);
    });
  }

  stopScreenShare(): Promise<void> {
    const sender = this.screenSender;
    if (!sender) return Promise.resolve();
    this.screenSender = null;
    sender.track?.stop();
    return this.withSignalingLock(() => this.pc?.removeTrack(sender));
  }

  startCamera(stream: MediaStream): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track || this.cameraSender) return Promise.resolve();
    return this.withSignalingLock(() => {
      if (this.pc && !this.cameraSender) this.cameraSender = this.pc.addTrack(track, stream);
    });
  }

  /** Another camera under the SAME sender: no renegotiation, the other side never sees video stop. */
  switchCamera(track: MediaStreamTrack): Promise<boolean> {
    return this.run(async () => {
      const sender = this.cameraSender;
      if (!sender) return false;
      const old = sender.track;
      await sender.replaceTrack(track);
      if (old && old !== track) old.stop();
      return true;
    });
  }

  stopCamera(): Promise<void> {
    const sender = this.cameraSender;
    if (!sender) return Promise.resolve();
    this.cameraSender = null;
    sender.track?.stop();
    return this.withSignalingLock(() => this.pc?.removeTrack(sender));
  }

  // MARK: - Remote media

  private onTrack(e: RTCTrackEvent): void {
    const track = e.track;
    if (track.kind === 'audio') {
      this.listener.remoteAudio(track);
      return;
    }
    const mid = e.transceiver?.mid ?? '';
    this.remoteVideoByMid.set(mid, track);
    track.addEventListener('ended', () => {
      if (this.remoteVideoByMid.get(mid) === track) {
        this.remoteVideoByMid.delete(mid);
        this.publishRemoteVideo();
      }
    });
    this.publishRemoteVideo();
  }

  /** A transceiver the SFU stopped sending on is a track it took away. */
  private pruneInactiveVideo(): void {
    const pc = this.pc;
    if (!pc) return;
    let changed = false;
    for (const [mid, track] of this.remoteVideoByMid) {
      const transceiver = pc.getTransceivers().find((t) => t.mid === mid && t.receiver.track === track);
      const receiving = transceiver && (transceiver.currentDirection === 'recvonly' || transceiver.currentDirection === 'sendrecv');
      if (!receiving) {
        this.remoteVideoByMid.delete(mid);
        changed = true;
      }
    }
    if (changed) this.publishRemoteVideo();
  }

  private streamFor(track: MediaStreamTrack): MediaStream {
    let stream = this.streamsByTrack.get(track.id);
    if (!stream) {
      stream = new MediaStream([track]);
      this.streamsByTrack.set(track.id, stream);
    }
    return stream;
  }

  /** Re-sorts every remote video track: the screen (one) or a camera (one per sender session). */
  private publishRemoteVideo(): void {
    let screen: MediaStream | null = null;
    const cameras: Record<string, MediaStream> = {};
    for (const [mid, track] of this.remoteVideoByMid) {
      const info = remoteTrackInfo(track.id);
      const type = info?.type ?? this.mediaMap[mid];
      if (type === 'screen') screen = this.streamFor(track);
      else if (type === 'video' && info?.sessionId) cameras[info.sessionId] = this.streamFor(track);
    }
    this.listener.remoteScreen(screen);
    this.listener.remoteVideos(cameras);
  }

  // MARK: - Negotiation

  private async offer(): Promise<void> {
    const pc = this.pc;
    if (!pc || pc.signalingState !== 'stable') return;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.listener.localSdp(JSON.stringify({ type: 'offer', sdp: pc.localDescription?.sdp ?? offer.sdp }));
    } catch (error) {
      if (!this.closed) this.listener.failed(`createOffer: ${String(error)}`);
    }
  }

  private async answer(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    try {
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.listener.localSdp(JSON.stringify({ type: 'answer', sdp: pc.localDescription?.sdp ?? answer.sdp }));
    } catch (error) {
      if (!this.closed) this.listener.failed(`createAnswer: ${String(error)}`);
    }
  }

  /**
   * The server-side signaling lock (calls-common's dcLocking): request over the data channel, retry
   * while denied, run the change, and mark that the NEXT answer settles it. Before the channel has
   * ever negotiated there is no lock to take and the action runs immediately.
   */
  /** One lock request in flight at a time (the response callback is a single slot). */
  private lockQueue: Promise<void> = Promise.resolve();

  private withSignalingLock(action: () => void): Promise<void> {
    const next = this.lockQueue.then(() => this.acquireLockAndRun(action));
    this.lockQueue = next.catch(() => undefined);
    return next;
  }

  private async acquireLockAndRun(action: () => void): Promise<void> {
    // The wait for the lock happens OFF the signaling chain: the SFU's own offers must still be
    // answered meanwhile, or it never releases the lock we are waiting for (Apple retries on its
    // queue without blocking it, for the same reason).
    const dc = this.dc;
    if (!this.dcNegotiated || !dc || dc.readyState !== 'open') {
      await this.run(action);
      return;
    }
    for (let attempt = 1; attempt <= CallRtcSession.lockMaxAttempts; attempt++) {
      if (this.closed) return;
      const acquired = await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => resolve(false), 2_000);
        this.lockResponse = (ok) => {
          clearTimeout(timer);
          resolve(ok);
        };
        this.sendDc(encodeDcLock());
      });
      this.lockResponse = null;
      if (acquired) {
        await this.run(() => {
          this.unlockAfterAnswer = true;
          action();
        });
        return;
      }
      await new Promise((r) => setTimeout(r, CallRtcSession.lockRetryDelayMillis));
    }
    // Proceeding beats hanging: worst case the SFU glares and the next offer recovers.
    await this.run(() => {
      this.unlockAfterAnswer = true;
      action();
    });
  }

  private sendDc(payload: Uint8Array): void {
    const dc = this.dc;
    if (!dc || dc.readyState !== 'open') return;
    try {
      dc.send(payload as unknown as ArrayBuffer);
    } catch {
      /* channel closing */
    }
  }

  private async onDcMessage(data: unknown): Promise<void> {
    const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data instanceof Uint8Array ? data : null;
    if (!bytes) return;
    const message = await decodeDcMessage(bytes);
    switch (message.kind) {
      case 'lockResponse':
        this.lockResponse?.(message.acquired);
        return;
      case 'mediaMap':
        this.mediaMap = message.typesByMid;
        // A mid the map no longer lists is a track the SFU took away.
        for (const mid of [...this.remoteVideoByMid.keys()]) if (!(mid in message.typesByMid)) this.remoteVideoByMid.delete(mid);
        this.publishRemoteVideo();
        return;
      case 'sdp':
        this.handleRemoteSignal(message.json);
        return;
      default:
        return;
    }
  }
}
